import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { StatusIndicator } from '@treams/treams-ui/common/StatusIndicator';
import { Tooltip } from '@treams/treams-ui/common/Tooltip';
import { tokens } from '@treams/treams-ui/theme/tokens';

const MARKED: Record<'MEDIUM' | 'HIGH', 'warning' | 'error'> = {
	MEDIUM: 'warning',
	HIGH: 'error',
};

const Slot = styled.span`
	position: relative;
	display: inline-flex;
	flex-shrink: 0;
	line-height: 0;
`;

const Mark = styled.span`
	position: absolute;
	right: 0;
	bottom: 0;
	z-index: 1;
	display: flex;
	box-sizing: content-box;
	line-height: 0;
	border: 2px solid ${tokens.border.white};
	border-radius: ${tokens.radius.full}px;
	background: ${tokens.surface.white};
`;

const isMarked = (risk?: string | null): risk is 'MEDIUM' | 'HIGH' =>
	risk === 'MEDIUM' || risk === 'HIGH';

/**
 * Corner dot for a medium or high retention risk. Low and an empty rating
 * stay unmarked. The parent slot is only as large as the avatar, so the dot
 * sits on the bottom-right edge. Hovering the dot explains the rating.
 * @param props component props
 * @param props.risk the placement's retention risk
 * @param props.children the avatar
 */
export default function TalentReviewRetentionRiskMark(props: {
	risk?: string | null;
	children: ReactNode;
}) {
	const { t } = useTranslation();

	if (!isMarked(props.risk)) {
		return <Slot>{props.children}</Slot>;
	}

	const explanation = `${t('talentReviews.v2.sidebar.card.retentionRiskLabel')}: ${t(
		`talentReviews.v2.sidebar.card.retentionRisk.${props.risk}`,
	)}`;

	return (
		<Slot>
			{props.children}
			<Mark>
				<Tooltip dark content={explanation} position="top" fitContent portal>
					<span role="img" aria-label={explanation}>
						<StatusIndicator state={MARKED[props.risk]} />
					</span>
				</Tooltip>
			</Mark>
		</Slot>
	);
}
